<script setup>
import { computed, shallowRef, useSlots, watch } from 'vue';
import UiCard from './UiCard.vue';
import UiColumnsMenu from './UiColumnsMenu.vue';
import UiRowActions from './UiRowActions.vue';
import UiViewToggle from './UiViewToggle.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import { matchesSearch } from '@/utils/search';

// The list page every module uses: a card with title, search, optional
// tabs and "Add" button, and a table running edge to edge. Pages pass
// columns and rows; cells are customised with the usual `item.<key>` slots.
//
// Controls sit on a toolbar row under the title: tabs and the page's
// `toolbar` filters on the left; search, the page's `actions` buttons and
// "Add" on the right. When nothing goes on the left, a row holding only
// right-aligned controls looks empty, so they move up beside the title.
// The `header-actions` slot always sits beside the title, for a switch that
// changes what the whole card lists (e.g. Employees / Parents on Accounts).
//
//   <UiTableCard title="Events" :headers="headers" :items="items"
//     :loading="loading" :error="error" add-label="Add event"
//     :row-actions="(row) => [{ title: 'Edit', onClick: () => edit(row) }]"
//     @add="create" />
//
// A page that also fills the `card` slot ({ item }) gets a table / grid
// switch: the grid shows one card per row (searched like the table, with the
// same ⋯ actions in the corner). The choice is remembered with columnsKey.
const props = defineProps({
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  headers: { type: Array, required: true },
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Something the user should know that isn't a failure, e.g. "You have no
  // advisory section yet." A string, or { title, text } for two lines.
  info: { type: [String, Object], default: null },
  // [{ id, name }]; bind the selection with v-model:tab
  tabs: { type: Array, default: () => [] },
  searchable: { type: Boolean, default: true },
  searchPlaceholder: { type: String, default: 'Search' },
  // Row fields the search also matches besides the columns, e.g. an email
  // or LRN shown under a name: ['email'].
  searchFields: { type: Array, default: () => [] },
  addLabel: { type: String, default: '' },
  emptyText: { type: String, default: 'No records yet.' },
  itemsPerPage: { type: Number, default: 10 },
  itemValue: { type: [String, Function], default: 'id' },
  // (row) => [{ title, icon?, destructive?, hidden?, onClick }]; adds the ⋯ column
  rowActions: { type: Function, default: null },
  // Field (or function) naming a row for the ⋯ button's screen-reader label
  rowLabel: { type: [String, Function], default: null },
  // Collapsible row groups, e.g. [{ key: 'day', order: 'asc' }]
  groupBy: { type: Array, default: undefined },
  // Start each group expanded instead of collapsed.
  groupsOpen: { type: Boolean, default: false },
  customKeySort: { type: Object, default: undefined },
  // Headers with `hideable: true` get a "Columns" menu to turn them off.
  // With a key the choice is remembered in this browser.
  columnsKey: { type: String, default: '' },
  // (row) => text for a row that isn't a record, e.g. a lunch break in a
  // timetable: it keeps its first cell and shows the text centred across the
  // rest of the row, shaded. Other rows (falsy text) render as usual.
  bandLabel: { type: Function, default: null },
});
defineEmits(['add']);
const tab = defineModel('tab', { type: [String, Number], default: null });

const slots = useSlots();
const search = shallowRef('');

const hasGrid = computed(() => !!slots.card);
const viewKey = computed(() => (props.columnsKey ? `table-view:${props.columnsKey}` : ''));
function readView() {
  try {
    return (viewKey.value && localStorage.getItem(viewKey.value)) === 'grid' ? 'grid' : 'table';
  } catch {
    return 'table';
  }
}
const view = shallowRef(readView());
watch(view, (value) => {
  if (!viewKey.value) return;
  try {
    localStorage.setItem(viewKey.value, value);
  } catch {
    // Private mode or blocked storage: the choice just isn't remembered.
  }
});
const showGrid = computed(() => hasGrid.value && view.value === 'grid');

// v-data-table keeps its open groups internal (no `opened` prop), so with
// groupsOpen each group is opened the first time its header renders. Groups
// already seen are skipped, so one the user collapses stays collapsed.
const seenGroups = new Set();
function openGroupOnce(group, isGroupOpen, toggleGroup) {
  if (!props.groupsOpen || seenGroups.has(group.id)) return;
  seenGroups.add(group.id);
  if (!isGroupOpen(group)) toggleGroup(group);
}
// The grid has no v-data-table to search for it: match the same columns
// and searchFields the table would.
const gridItems = computed(() => {
  const keys = [...props.headers.map((h) => h.key), ...props.searchFields];
  return props.items.filter((item) => matchesSearch(search.value, keys.map((key) => item?.[key])));
});
// v-data-table only searches column values; also match props.searchFields.
const customFilter = computed(() =>
  props.searchFields.length ? (value, query, item) => matchesSearch(query, [value, ...props.searchFields.map((key) => item?.raw?.[key])]) : undefined,
);
const hideableColumns = computed(() => props.headers.filter((h) => h.hideable));
const storageKey = computed(() => (props.columnsKey ? `table-columns:${props.columnsKey}` : ''));
function readHidden() {
  if (!storageKey.value) return [];
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey.value) || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}
const hidden = shallowRef(readHidden());
watch(hidden, (keys) => {
  if (!storageKey.value) return;
  try {
    localStorage.setItem(storageKey.value, JSON.stringify(keys));
  } catch {
    // Private mode or blocked storage: the choice just isn't remembered.
  }
});

const hasStart = computed(() => props.tabs.length > 1 || !!slots.toolbar);
const hasEnd = computed(
  () => props.searchable || !!props.addLabel || !!slots.actions || hideableColumns.value.length > 0 || hasGrid.value,
);

const columns = computed(() => {
  const shown = props.headers.filter((h) => !hidden.value.includes(h.key));
  return props.rowActions
    ? [...shown, { title: '', key: '__actions', align: 'end', sortable: false, width: 72 }]
    : shown;
}
);

// Forward the page's cell slots (item.<key>, etc.) to v-data-table.
const tableSlots = computed(() =>
  Object.keys(slots).filter((name) => !['actions', 'header-actions', 'toolbar', 'no-data', 'card'].includes(name)),
);
// Just the cell slots, for rows drawn through the `item` slot (bandLabel).
const cellSlots = computed(() => tableSlots.value.filter((name) => name.startsWith('item.')));

function labelFor(row) {
  if (typeof props.rowLabel === 'function') return `Actions for ${props.rowLabel(row)}`;
  if (props.rowLabel) return `Actions for ${row[props.rowLabel]}`;
  return 'Row actions';
}
</script>

<template>
  <UiCard :title="title" :description="description" flush>
    <template v-if="hasStart" #toolbar>
      <PageTabs v-if="tabs.length > 1" v-model="tab" :items="tabs" />
      <slot name="toolbar" />
      <div v-if="hasEnd" class="ui-table-card__end">
        <v-text-field
          v-if="searchable"
          v-model="search"
          :placeholder="searchPlaceholder"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
          class="card-search"
        />
        <UiViewToggle v-if="hasGrid" v-model="view" />
        <UiColumnsMenu v-if="hideableColumns.length && !showGrid" v-model="hidden" :columns="hideableColumns" />
        <slot name="actions" />
        <v-btn v-if="addLabel" color="primary" prepend-icon="mdi-plus" @click="$emit('add')">
          {{ addLabel }}
        </v-btn>
      </div>
    </template>
    <template v-if="slots['header-actions'] || (!hasStart && hasEnd)" #actions>
      <slot name="header-actions" />
      <template v-if="!hasStart && hasEnd">
        <v-text-field
          v-if="searchable"
          v-model="search"
          :placeholder="searchPlaceholder"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          hide-details
          clearable
          class="card-search"
        />
        <UiViewToggle v-if="hasGrid" v-model="view" />
        <UiColumnsMenu v-if="hideableColumns.length && !showGrid" v-model="hidden" :columns="hideableColumns" />
        <slot name="actions" />
        <v-btn v-if="addLabel" color="primary" prepend-icon="mdi-plus" @click="$emit('add')">
          {{ addLabel }}
        </v-btn>
      </template>
    </template>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mx-6 mb-4">
      {{ error }}
    </v-alert>
    <v-alert
      v-else-if="info"
      type="info"
      variant="tonal"
      icon="mdi-information-outline"
      :title="typeof info === 'string' ? undefined : info.title"
      class="mx-6 mb-4"
    >
      {{ typeof info === 'string' ? info : info.text }}
    </v-alert>

    <template v-if="showGrid">
      <div v-if="loading" class="ui-table-card__grid-status">
        <v-progress-circular indeterminate size="24" width="2" />
      </div>
      <div v-else-if="gridItems.length" class="ui-table-card__grid">
        <div
          v-for="item in gridItems"
          :key="typeof itemValue === 'function' ? itemValue(item) : item[itemValue]"
          class="ui-table-card__tile"
        >
          <div v-if="rowActions" class="ui-table-card__tile-actions">
            <UiRowActions :label="labelFor(item)" :items="rowActions(item)" />
          </div>
          <slot name="card" :item="item" />
        </div>
      </div>
      <div v-else class="ui-table-card__grid-status table-flush">
        <slot name="no-data">
          <div class="table-empty">{{ emptyText }}</div>
        </slot>
      </div>
    </template>
    <v-data-table
      v-else
      :headers="columns"
      :items="items"
      :item-value="itemValue"
      :search="search"
      :custom-filter="customFilter"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :group-by="groupBy"
      :custom-key-sort="customKeySort"
      class="table-flush"
    >
      <template v-if="groupBy" #group-header="{ item, columns, toggleGroup, isGroupOpen }">
        <tr class="ui-table-card__group" :ref="() => openGroupOnce(item, isGroupOpen, toggleGroup)">
          <td :colspan="columns.length">
            <button type="button" class="ui-table-card__group-btn" @click="toggleGroup(item)">
              <v-icon size="18">{{ isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              {{ item.value }}
              <span class="ui-table-card__group-count">{{ item.items?.length }}</span>
            </button>
          </td>
        </tr>
      </template>
      <template v-for="name in tableSlots" :key="name" #[name]="scope">
        <slot :name="name" v-bind="scope || {}" />
      </template>
      <template v-if="rowActions" #[`item.__actions`]="{ item }">
        <UiRowActions :label="labelFor(item)" :items="rowActions(item)" />
      </template>
      <!-- Band rows (see bandLabel); every other row is v-data-table's own,
           with the same cell slots. -->
      <template v-if="bandLabel" #item="scope">
        <tr v-if="bandLabel(scope.item)" class="v-data-table__tr table-row--static ui-table-card__band">
          <td class="v-data-table__td">
            <slot :name="`item.${scope.columns[0].key}`" v-bind="{ ...scope, value: scope.item[scope.columns[0].key] }">
              {{ scope.item[scope.columns[0].key] }}
            </slot>
          </td>
          <td :colspan="scope.columns.length - 1" class="v-data-table__td ui-table-card__band-label">
            {{ bandLabel(scope.item) }}
          </td>
        </tr>
        <v-data-table-row v-else v-bind="scope.props">
          <template v-for="name in cellSlots" :key="name" #[name]="cell">
            <slot :name="name" v-bind="cell || {}" />
          </template>
          <template v-if="rowActions" #[`item.__actions`]="{ item }">
            <UiRowActions :label="labelFor(item)" :items="rowActions(item)" />
          </template>
        </v-data-table-row>
      </template>
      <template #no-data>
        <slot name="no-data">
          <div class="table-empty">{{ emptyText }}</div>
        </slot>
      </template>
    </v-data-table>
  </UiCard>
</template>

<style scoped>
.ui-table-card__end {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.ui-table-card__group-count {
  font-weight: 400;
  color: var(--muted-foreground);
}
.ui-table-card__group td {
  background-color: var(--subtle);
}
.ui-table-card__group-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 600;
  color: var(--foreground);
  cursor: pointer;
}
/* Grid view */
.ui-table-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding: 0 24px 24px;
}
.ui-table-card__tile {
  position: relative;
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background-color: var(--card);
}
.ui-table-card__tile-actions {
  position: absolute;
  top: 8px;
  right: 8px;
}
.ui-table-card__grid-status {
  display: flex;
  justify-content: center;
  padding: 24px;
}
@media (max-width: 600px) {
  .ui-table-card__grid {
    padding: 0 16px 16px;
  }
  .ui-table-card__end {
    width: 100%;
    margin-left: 0;
  }
}
/* A band row (bandLabel): a soft wash of the school's brand colour, its
   text centred across the row. */
.ui-table-card__band > td {
  background: var(--brand-soft);
}
.ui-table-card__band-label {
  text-align: center;
  font-weight: 600;
  font-style: italic;
  color: var(--brand);
}
</style>
